/**
 * TemplateSettingsPanel — v5 exporter template editor.
 *
 * Three tabs:
 *  - Vorschau:        rendered iframe using renderKundenKonformitaet() with
 *                     placeholder values — shows the exact same document as export
 *  - HTML-Quelltext:  textarea editor for the backend html_source (auto-saves)
 *  - Spaltenbelegung: table of variable → expression mappings
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import { api } from "../../../api/client"
import { renderKundenKonformitaet } from "../../../lib/templates/html/kunden_konformitaet"
import { localeTagFor } from "../../../utils/locale"

// Sample data with readable placeholder labels so the preview looks like
// the real document but with field names where values would appear.
const PREVIEW_HTML = renderKundenKonformitaet({
  issuer: "{{issuer}}",
  issuerAddress: "{{issuerAddress}}",
  creationDate: "{{today}}",
  signatory: "{{signatory}}",
  signatoryRole: "{{signatoryRole}}",
  signatoryPlace: "{{signatoryPlace}}",
  signatoryDate: "{{signatoryDate}}",
  customer: {
    customerId: "{{customer_id}}",
    customerName: "{{customer_name}}",
    isPositive: true,
    totalProductCount: 2,
    svhcProductCount: 2,
    products: [
      {
        productId: "PROD-001",
        productName: "{{product_name}}",
        rows: [
          {
            casNumber: "{{cas_number}}",
            substanceName: "{{substance_name}}",
            svhcGehalt: "{{svhc_concentration}}",
            locationInComponent: "{{location_in_component}}",
            componentName: "{{component_name}}",
          },
          {
            casNumber: "{{cas_number}}",
            substanceName: "{{substance_name_2}}",
            svhcGehalt: "{{svhc_concentration_2}}",
            locationInComponent: "{{location_in_component_2}}",
            componentName: "{{component_name_2}}",
          },
        ],
      },
    ],
  },
})

type Tab = "vorschau" | "quelltext" | "mapping"

export function TemplateSettingsPanel({ apId }: { apId: string }) {
  const [activeTab, setActiveTab] = useState<Tab>("vorschau")
  const { translate, currentLanguage } = useTranslation()
  const localeTag = localeTagFor(currentLanguage)
  const queryClient = useQueryClient()

  const { data: template, isLoading } = useQuery({
    queryKey: ["exporter-template", apId],
    queryFn: () => api.exporterTemplateGet(apId),
    staleTime: 60_000,
  })

  const [htmlSource, setHtmlSource] = useState("")
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({})
  const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (template) {
      setHtmlSource(template.html_source)
      setColumnMapping(template.column_mapping ?? {})
    }
  }, [template])

  const debouncedPatch = useCallback(
    (patch: {
      html_source?: string
      column_mapping?: Record<string, string>
    }) => {
      if (patchTimerRef.current) clearTimeout(patchTimerRef.current)
      patchTimerRef.current = setTimeout(async () => {
        try {
          await api.exporterTemplatePatch(apId, patch)
          queryClient.invalidateQueries({
            queryKey: ["exporter-template", apId],
          })
        } catch {
          // best-effort
        }
      }, 600)
    },
    [apId, queryClient],
  )

  const handleHtmlBlur = () => {
    if (template && htmlSource !== template.html_source) {
      debouncedPatch({ html_source: htmlSource })
    }
  }

  const handleMappingChange = (key: string, newExpr: string) => {
    const next = { ...columnMapping, [key]: newExpr }
    setColumnMapping(next)
    debouncedPatch({ column_mapping: next })
  }

  const handleAddMapping = () => {
    const key = window.prompt(translate("QMS/TEMPLATE/PROMPT_VAR_KEY"))
    if (!key?.trim()) return
    const expr = window.prompt(translate("QMS/TEMPLATE/PROMPT_VAR_EXPR")) ?? ""
    const next = { ...columnMapping, [key.trim()]: expr.trim() }
    setColumnMapping(next)
    debouncedPatch({ column_mapping: next })
  }

  const handleRemoveMapping = (key: string) => {
    const next = { ...columnMapping }
    delete next[key]
    setColumnMapping(next)
    debouncedPatch({ column_mapping: next })
  }

  const handleReset = async () => {
    if (!window.confirm(translate("QMS/TEMPLATE/RESET_CONFIRM"))) return
    try {
      const tmpl = await api.exporterTemplateReset(apId)
      setHtmlSource(tmpl.html_source)
      setColumnMapping(tmpl.column_mapping ?? {})
      queryClient.invalidateQueries({ queryKey: ["exporter-template", apId] })
    } catch (err) {
      window.alert(
        err instanceof Error
          ? err.message
          : translate("QMS/TEMPLATE/RESET_FAILED"),
      )
    }
  }

  if (isLoading) {
    return (
      <p className="text-xs italic text-grey5">
        {translate("QMS/TEMPLATE/LOADING")}
      </p>
    )
  }
  if (!template) {
    return (
      <p className="text-red-400 text-xs">
        {translate("QMS/TEMPLATE/NOT_FOUND")}
      </p>
    )
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "vorschau", label: translate("QMS/TEMPLATE/TAB_PREVIEW") },
    { id: "quelltext", label: translate("QMS/TEMPLATE/TAB_SOURCE") },
    { id: "mapping", label: translate("QMS/TEMPLATE/TAB_MAPPING") },
  ]

  return (
    <div className="overflow-hidden rounded-lg border border-grey4 bg-grey1">
      {/* Tab bar */}
      <div className="flex border-b border-grey4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? "border-b-2 border-yellow1 text-yellow1"
                : "text-grey5 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className={activeTab === "vorschau" ? "" : "p-3"}>
        {/* Vorschau: renderKundenKonformitaet() with placeholder values */}
        {activeTab === "vorschau" && (
          <div className="flex flex-col">
            <p className="px-3 pb-2 pt-3 text-xs text-grey5">
              {translate("QMS/TEMPLATE/PREVIEW_HINT")}
            </p>
            <div className="relative" style={{ height: "480px" }}>
              <iframe
                srcDoc={PREVIEW_HTML}
                className="h-full w-full border-0 bg-white"
                sandbox="allow-same-origin"
                title={translate("QMS/TEMPLATE/PREVIEW_IFRAME_TITLE")}
              />
            </div>
          </div>
        )}

        {/* HTML-Quelltext: editable textarea */}
        {activeTab === "quelltext" && (
          <div>
            <p className="mb-2 text-xs text-grey5">
              {translate("QMS/TEMPLATE/SOURCE_HINT")}
            </p>
            <textarea
              value={htmlSource}
              onChange={(e) => setHtmlSource(e.target.value)}
              onBlur={handleHtmlBlur}
              rows={18}
              spellCheck={false}
              className="font-mono w-full resize-y rounded border border-grey4 bg-grey2 px-2 py-2 text-[11px] text-grey6 outline-hidden transition-colors focus:border-yellow1"
            />
          </div>
        )}

        {/* Spaltenbelegung */}
        {activeTab === "mapping" && (
          <div>
            <p className="mb-2 text-xs text-grey5">
              {translate("QMS/TEMPLATE/MAPPING_HINT")}
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-grey4">
                  <th className="py-1.5 pr-3 text-left font-semibold text-grey5">
                    {translate("QMS/TEMPLATE/COL_VARIABLE")}
                  </th>
                  <th className="py-1.5 text-left font-semibold text-grey5">
                    {translate("QMS/TEMPLATE/COL_SOURCE")}
                  </th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody>
                {Object.entries(columnMapping).map(([key, expr]) => (
                  <tr key={key} className="border-b border-grey4/40">
                    <td className="font-mono py-1.5 pr-3 text-yellow1">{`{{${key}}}`}</td>
                    <td className="py-1.5">
                      <input
                        value={expr}
                        onChange={(e) =>
                          handleMappingChange(key, e.target.value)
                        }
                        onBlur={() =>
                          debouncedPatch({ column_mapping: columnMapping })
                        }
                        className="font-mono w-full rounded border border-grey4 bg-grey2 px-2 py-0.5 text-grey6 outline-hidden transition-colors focus:border-yellow1"
                      />
                    </td>
                    <td className="py-1.5 pl-2">
                      <button
                        onClick={() => handleRemoveMapping(key)}
                        className="hover:text-red-400 text-grey5 transition-colors"
                        title={translate("QMS/TEMPLATE/REMOVE_VARIABLE")}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={handleAddMapping}
              className="mt-2 text-xs text-grey5 transition-colors hover:text-white"
            >
              {translate("QMS/TEMPLATE/ADD_VARIABLE")}
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-grey4 px-3 py-2">
        <span className="text-[11px] text-grey5">
          {translate("QMS/TEMPLATE/FOOTER_VERSION", {
            version: template.seed_version,
            date: new Date(template.updated_at).toLocaleDateString(localeTag),
          })}
        </span>
        <button
          onClick={handleReset}
          className="hover:text-red-400 text-xs text-grey5 transition-colors"
        >
          {translate("QMS/TEMPLATE/RESET")}
        </button>
      </div>
    </div>
  )
}
